import { getGoogleSignOutURL } from './consts.mjs'
import HubSpotService from './hubspot'
import {
  kissmetricsRecordingEvent,
  KISSMETRICS_TRACK_EVENTS,
  PROPERTY_NAMES,
} from './kissmetrics'
import { getParentLocId, isGlobal } from './locations'
import { Mixpanel } from './mixpanel'
import {
  clearToken,
  getDailyReportInfo,
  removeAIWPhaseOutModalStatus,
  removeAdblockDetectModalStatus,
  removeUpdateBillingInfoAlert,
  setDailyReportInfo,
} from './storage'
import { getLanguageCode } from './translation'

export const getLoggedUser = () => JSON.parse(localStorage.getItem('user'))

export const isSignedIn = () => localStorage.getItem('user') !== null

const identifyHubSpot = (user) => {
  HubSpotService.instance.setIdentity(user.email, {
    id: user.id,
  })
}

const identifyKissmetric = (user) => {
  const oldUser = JSON.parse(localStorage.getItem('user'))
  if (!oldUser || !oldUser.signInTracked) {
    let userType = 'free'
    let tier
    let billingPlan

    if (user.subscription) {
      if (
        user.subscription.subscriptionStatus === 'active' ||
        user.subscription.subscriptionStatus === 'paid'
      ) {
        userType = 'paid'
      } else if (user.subscription.subscriptionStatus === 'trialing') {
        userType = 'trial'
      }
      tier = user.subscription.tier
      billingPlan = user.subscription.planInterval
    }

    kissmetricsRecordingEvent(
      KISSMETRICS_TRACK_EVENTS.signed_in,
      {
        [PROPERTY_NAMES.user_type]: userType,
        [PROPERTY_NAMES.plan_tier]: tier,
        [PROPERTY_NAMES.billing_plan]: billingPlan,
        [PROPERTY_NAMES.language_preference]: getLanguageCode(),
      },
      true,
    )
  }
  // eslint-disable-next-line no-param-reassign
  user.signInTracked = true
}

export const signIn = (user) => {
  identifyKissmetric(user)
  identifyHubSpot(user)

  localStorage.setItem('user', JSON.stringify(user))
  localStorage.removeItem('addons')
  localStorage.removeItem('subscription')

  if (user.limits?.reports) {
    const reportInfo = getDailyReportInfo()
    setDailyReportInfo({ ...reportInfo, reports_limits: user.limits.reports })
  }

  if (user.subscription) {
    kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.billing_status, {
      [PROPERTY_NAMES.billing_status]: user.subscription.subscriptionStatus,
    })
  }
}

export const updateUserLimitsStorage = (limits) => {
  const user = getLoggedUser()
  if (user) {
    user.limits = limits
    localStorage.setItem('user', JSON.stringify(user))
  }
}

const skipSignOut = ['/settings', '/competitor_tracking', '/position_tracking']

export const signOut = (next) => {
  if (isSignedIn()) {
    // remove this first since it is using `user.id`
    removeUpdateBillingInfoAlert()

    window.localStorage.removeItem('user')
    clearToken()
    window.localStorage.removeItem('last_lifetime_offer_date')
    window.localStorage.removeItem('addons')
    window.localStorage.removeItem('subscription')
    window.sessionStorage.removeItem('bulk_analysis_results')
    window.sessionStorage.removeItem('bulk_analysis_keywords')

    removeAdblockDetectModalStatus()
    removeAIWPhaseOutModalStatus()
    Mixpanel.reset()

    if (next) {
      window.location.href = next
    } else {
      window.location.href = getGoogleSignOutURL({
        next: skipSignOut.some(
          (url) => window.location.pathname.indexOf(url) > -1,
        )
          ? `/dashboard`
          : window.location.pathname + window.location.search,
      })
    }
  }
}

export const isAdminUser = (currentUser) => {
  const user = currentUser || getLoggedUser()
  return user.user_group === 'cs_team_group'
}

export const isEnterpriseAdmin = (currentUser) => {
  const user = currentUser || getLoggedUser()
  return user?.enterprise_admin
}

export const isEnterpriseUser = (currentUser) => {
  const user = currentUser || getLoggedUser()
  return user?.enterprise_id && !isEnterpriseAdmin(currentUser)
}

export const isSubUser = (currentUser) => {
  const user = currentUser || getLoggedUser()
  return user?.user_group === 'multi_user'
}

export const getMainUser = (currentUser) => {
  const user = currentUser || getLoggedUser()
  return user.mainUser
}

export const shouldUpdateBillingInfo = () => {
  const user = getLoggedUser()
  return !user?.payment_method?.object
}

export const isConfirmedUser = () => {
  const user = getLoggedUser()
  return user.confirmed === true
}

export const getUserPreferredLangLoc = (
  allowCities = false,
  cachedLocations = undefined,
) => {
  const user = getLoggedUser()
  if (user?.preferences?.locId && !isGlobal(user?.preferences?.locId)) {
    return `${user.preferences.lang}::${
      allowCities
        ? user.preferences.locId
        : getParentLocId(user.preferences.locId, cachedLocations)
    }`
  }
  return `en::2840`
}

export const getUserFreeTrialDays = () => {
  if (isSignedIn()) {
    const user = getLoggedUser()
    return typeof user.free_trial_days === 'undefined' ||
      user.free_trial_days === 0
      ? 7
      : user.free_trial_days
  }
  return 7
}

export const pushLoginLimitMessage = () => {
  localStorage.setItem('LOGIN_LIMIT_REACHED', true)
}

export const popLoginLimitMessage = () => {
  const data = localStorage.getItem('LOGIN_LIMIT_REACHED')
  localStorage.removeItem('LOGIN_LIMIT_REACHED')
  return data
}

const dec2hex = (dec) => {
  return dec.toString(16).padStart(2, '0')
}

export const text2sha = async ({ algorithm = 'SHA-256', text }) => {
  return Array.from(
    new Uint8Array(
      await crypto.subtle.digest(
        algorithm,
        new TextEncoder('utf-8').encode(text),
      ),
    ),
    dec2hex,
  ).join('')
}

export const generate_ubs_app_header = async () => {
  const arr = new Uint8Array(32)
  window.crypto.getRandomValues(arr)
  const nonce = Array.from(arr, dec2hex).join('')
  const pass = await text2sha({
    text: nonce + process.env.REACT_APP_UBS_APP_KEY,
  })
  return `${pass}${nonce}`
}
